import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Video, X, Clock, BellRing } from "lucide-react";

/**
 * Sticky banner + auto-join for the nearest confirmed booking.
 *
 * TRIGGER RULES:
 *   1. Time-based: shows within `warnMinutes` of start_time (default 30 min).
 *   2. Event-based: shows IMMEDIATELY (even hours before start_time) if the
 *      counselor has pressed "join" — booking.counselor_ready_at is set by the
 *      backend `/api/bookings/:id/counselor-ready` endpoint.
 *
 * DELIVERY LAYERS:
 *   - In-page sticky card (this component).
 *   - OS-level `Notification` API when the tab is backgrounded (requires
 *     permission; asked softly when a session becomes eligible).
 *   - Backend fires a Resend email in parallel (handled server-side).
 *
 * AUTO-NAV:
 *   - At start_time (or immediately when counselor_ready_at flips on),
 *     navigates to /session/:id ONCE per booking (sessionStorage guard).
 */
export default function SessionAutoJoin({ bookings, viewerRole = "client", warnMinutes = 30 }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [now, setNow] = useState(() => new Date());
  const [dismissedAuto, setDismissedAuto] = useState(false);
  const notifiedRef = useRef(new Set());
  const permissionAskedRef = useRef(false);

  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Find the nearest confirmed booking that hasn't ended.
  const target = useMemo(() => {
    if (!bookings?.length) return null;
    const candidates = bookings
      .filter(b => b.status === "confirmed" && b.date && b.start_time)
      .map(b => {
        const start = new Date(`${b.date}T${b.start_time}:00`);
        const end = b.end_time
          ? new Date(`${b.date}T${b.end_time}:00`)
          : new Date(start.getTime() + 45 * 60 * 1000);
        return { b, start, end };
      })
      .filter(x => x.end.getTime() > now.getTime() - 60 * 1000)
      .sort((a, b) => a.start - b.start);
    return candidates[0] || null;
  }, [bookings, now]);

  const msUntilStart = target ? target.start.getTime() - now.getTime() : Infinity;
  const withinTimeWindow = msUntilStart <= warnMinutes * 60 * 1000 && msUntilStart > -60 * 60 * 1000;
  const counselorReady = Boolean(target?.b?.counselor_ready_at);
  const clientAlreadyIn = Boolean(target?.b?.session_joined_at);
  // Who else is *currently* in the room right now (from session_events).
  const activeRoles = target?.b?.active_roles || [];
  const otherRole = viewerRole === "counselor" ? "client" : "counselor";
  const otherInRoom = activeRoles.includes(otherRole);
  const meInRoom = activeRoles.includes(viewerRole);
  const windowState = target?.b?.window_state; // "future" | "in_window" | ...
  const inValidWindow = windowState === "in_window";
  const withinWarn = (withinTimeWindow || (counselorReady && !clientAlreadyIn) || (otherInRoom && inValidWindow))
                     && !meInRoom;
  const inSession = target && now.getTime() >= target.start.getTime() && now.getTime() < target.end.getTime();

  // Ask for browser Notification permission softly when a session becomes eligible.
  useEffect(() => {
    if (!target || !withinWarn) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (permissionAskedRef.current) return;
    if (Notification.permission === "default") {
      permissionAskedRef.current = true;
      // Defer to next tick so it doesn't feel like an "on-load" popup.
      setTimeout(() => {
        try { Notification.requestPermission().catch(() => {}); } catch (_) {}
      }, 800);
    }
  }, [target, withinWarn]);

  // Fire OS-level notification when counselor_ready_at first appears for this booking.
  useEffect(() => {
    if (!target || !counselorReady || clientAlreadyIn) return;
    if (viewerRole === "counselor") return; // counselor doesn't need to be told
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const key = `notified:counselor-ready:${target.b.id}`;
    if (notifiedRef.current.has(target.b.id)) return;
    try {
      if (localStorage.getItem(key)) {
        notifiedRef.current.add(target.b.id);
        return;
      }
      const n = new Notification("Your counselor is ready", {
        body: `${target.b.counselor_name || "Your counselor"} is in the room for ${target.b.service_name}. Tap to join.`,
        tag: `counselor-ready-${target.b.id}`,
        icon: "/favicon.svg",
      });
      n.onclick = () => {
        window.focus();
        nav(`/session/${target.b.id}`);
        n.close();
      };
      localStorage.setItem(key, String(Date.now()));
      notifiedRef.current.add(target.b.id);
    } catch (e) {
      // Some browsers throw if invoked without a user gesture on some pages; safe to ignore.
      console.warn("Notification fire failed:", e?.message || e);
    }
  }, [target, counselorReady, clientAlreadyIn, viewerRole, nav]);

  // Fire an OS-level notification the moment the OTHER participant appears in
  // the room during the valid window — covers first join AND rejoin after drop.
  // Deduped per (booking, join-event-timestamp) so a rejoin fires fresh.
  const latestOtherJoinTs = useMemo(() => {
    const events = target?.b?.session_events || [];
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (ev?.type === "joined" && ev?.actor_role === otherRole) return ev.at;
    }
    return null;
  }, [target, otherRole]);
  useEffect(() => {
    if (!target || !otherInRoom || !inValidWindow || meInRoom) return;
    if (!latestOtherJoinTs) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const key = `notified:other-joined:${target.b.id}:${latestOtherJoinTs}`;
    try {
      if (localStorage.getItem(key)) return;
      const who = otherRole === "counselor"
        ? (target.b.counselor_name || "Your counselor")
        : (target.b.client_name || "Your client");
      const n = new Notification(`${who} just joined`, {
        body: `${target.b.service_name} · Tap to open the room.`,
        tag: `other-joined-${target.b.id}`,
        icon: "/favicon.svg",
      });
      n.onclick = () => {
        window.focus();
        nav(`/session/${target.b.id}`);
        n.close();
      };
      localStorage.setItem(key, String(Date.now()));
    } catch (e) {
      console.warn("other-joined notification failed:", e?.message || e);
    }
  }, [target, otherInRoom, inValidWindow, meInRoom, latestOtherJoinTs, otherRole, nav]);

  // Auto-navigate ONCE at start OR when counselor is ready.
  useEffect(() => {
    if (!target || dismissedAuto) return;
    if (loc.pathname.startsWith(`/session/${target.b.id}`)) return;

    const key = `autojoined:${target.b.id}`;
    if (sessionStorage.getItem(key)) return;

    // Event-driven: counselor arrived before/at start — jump immediately.
    // Guard: this branch only applies to clients. The counselor is the one
    // who sets counselor_ready_at (by opening VideoSession), so they should
    // never be auto-navigated by this signal — only by the time-based path.
    if (counselorReady && !clientAlreadyIn && viewerRole !== "counselor") {
      sessionStorage.setItem(key, "1");
      nav(`/session/${target.b.id}`);
      return;
    }

    // Time-based: original behavior — jump at scheduled start_time.
    if (msUntilStart > 5_000) return;
    if (msUntilStart < -30_000) return;
    sessionStorage.setItem(key, "1");
    nav(`/session/${target.b.id}`);
  }, [target, msUntilStart, counselorReady, clientAlreadyIn, dismissedAuto, loc.pathname, nav]);

  if (!target || !withinWarn) return null;
  if (loc.pathname.startsWith(`/session/${target.b.id}`)) return null;

  const totalSecs = Math.max(0, Math.floor(msUntilStart / 1000));
  const mm = Math.floor(totalSecs / 60);
  const ss = totalSecs % 60;
  const withWhom =
    viewerRole === "counselor"
      ? (target.b.client_name || "your client")
      : (target.b.counselor_name || "your counselor");

  const headline = otherInRoom && inValidWindow && !meInRoom
    ? (viewerRole === "counselor" ? "Your client just joined — Join session" : "Your counselor is in the room — Join now")
    : counselorReady && !inSession && viewerRole !== "counselor"
      ? "Your counselor is ready — Join now"
      : inSession
        ? "Session in progress"
        : "Your session is about to begin";

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm" data-testid="session-autojoin-banner">
      <div className="card-soft p-4 shadow-xl"
           style={{border:"2px solid var(--brand)", background:"#fff"}}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
               style={{background:"rgba(91,117,83,0.12)"}}>
            {counselorReady && !inSession && viewerRole !== "counselor"
              ? <BellRing className="w-5 h-5" style={{color:"var(--brand)"}}/>
              : <Video className="w-5 h-5" style={{color:"var(--brand)"}}/>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif-display text-lg leading-tight" data-testid="autojoin-headline">
              {headline}
            </div>
            <div className="text-xs mt-0.5" style={{color:"var(--text-secondary)"}}>
              {target.b.service_name} · with {withWhom}
            </div>
            {!inSession && !counselorReady && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold"
                   style={{color:"var(--brand)"}} data-testid="autojoin-countdown">
                <Clock className="w-4 h-4"/>
                Starts in {String(mm).padStart(2,"0")}:{String(ss).padStart(2,"0")}
                {!dismissedAuto && msUntilStart <= 60_000 && msUntilStart > 0 && (
                  <span className="ml-1 text-xs" style={{color:"var(--text-secondary)"}}>
                    · joining automatically
                  </span>
                )}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="autojoin-join-now"
                onClick={() => nav(`/session/${target.b.id}`)}
                className="btn-primary text-sm">
                <Video className="w-4 h-4 inline mr-1"/>
                {counselorReady && !inSession && viewerRole !== "counselor" ? "Join now" : inSession ? "Join now" : "Join early"}
              </button>
              {!dismissedAuto && !inSession && !counselorReady && (
                <button data-testid="autojoin-dismiss"
                  onClick={() => setDismissedAuto(true)}
                  className="btn-outline text-sm">
                  Cancel auto-join
                </button>
              )}
            </div>
          </div>
          <button aria-label="Hide reminder"
            data-testid="autojoin-close"
            onClick={() => setDismissedAuto(true)}
            className="ml-1 opacity-50 hover:opacity-100 transition-opacity">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
}
